import { type ReactNode, useCallback, useRef } from "react";
import { isEqual } from "lodash";
import { type UseFormReturn } from "react-hook-form";
import { useRouteMatch } from "react-router-dom";
import { usePage } from "@/react/components/SettingsApp/Site/Pages/hooks/usePage";
import { useCustomThemeConfirmationModal } from "@circle-react-shared/BuilderUI/SiteThemes/CustomThemeConfirmationModal";
import { usePageThemeConfirmationModal } from "@circle-react-shared/BuilderUI/SiteThemes/PageThemeConfirmationModal";
import { useSiteThemeConfirmationModal } from "@circle-react-shared/BuilderUI/SiteThemes/SiteThemeConfirmationModal";
import {
  type BaseThemeFormData,
  BaseThemeFormWrapper,
  useThemeFormHelpers,
} from "@circle-react-shared/BuilderUI/Theme/BaseThemeFormWrapper";
import { useIsCustomFontsEnabled } from "../Hooks/useIsCustomFontsEnabled";
import type { CssVariables } from "../SiteThemes/constants";
import { FormWrapperFooter } from "./FormWrapperFooter";
import { FONT_FAMILY_OPTIONS } from "./SiteStyles/Tokens/Text/TextStyles/constants";
import { CSS_VARIABLE_LOOKUP } from "./constants";

export interface FormData extends BaseThemeFormData {
  theme: CssVariables;
}

interface FormWrapperProps {
  children: ReactNode;
}

interface GoogleFontWithWeights {
  family: string;
  weights: Set<string>;
}

const TYPOGRAPHY_VARIABLES = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p1",
  "p2",
  "p3",
];

const isGoogleFont = (
  fontFamily: string,
  webFontValues: Set<string>,
): boolean => !webFontValues.has(fontFamily);

const extractFontWeight = (
  tokenData: Record<string, unknown>,
  fontFamilyKey: string,
): string | null => {
  const weightKey = fontFamilyKey.replace("-font-family", "-font-weight");
  const fontWeight = tokenData[weightKey];
  return typeof fontWeight === "string" && fontWeight ? fontWeight : null;
};

const processTypographyToken = (
  tokenData: Record<string, unknown>,
  webFontValues: Set<string>,
  googleFontsMap: Map<string, Set<string>>,
): void => {
  for (const [key, value] of Object.entries(tokenData)) {
    if (!key.includes("-font-family") || typeof value !== "string" || !value) {
      continue;
    }

    if (!isGoogleFont(value, webFontValues)) {
      continue;
    }

    const fontWeight = extractFontWeight(tokenData, key);
    if (!fontWeight) {
      continue;
    }

    if (!googleFontsMap.has(value)) {
      googleFontsMap.set(value, new Set());
    }
    googleFontsMap.get(value)?.add(fontWeight);
  }
};

const deriveGoogleFontsFromFormData = (
  formData: CssVariables,
): GoogleFontWithWeights[] => {
  const googleFontsMap = new Map<string, Set<string>>();
  const webFontValues = new Set(FONT_FAMILY_OPTIONS.map(font => font.value));

  for (const typographyKey of TYPOGRAPHY_VARIABLES) {
    const tokenData = formData[typographyKey];
    if (typeof tokenData === "object" && tokenData != null) {
      processTypographyToken(tokenData, webFontValues, googleFontsMap);
    }
  }

  return Array.from(googleFontsMap.entries()).map(([family, weights]) => ({
    family,
    weights,
  }));
};

export const FormWrapper = ({ children }: FormWrapperProps) => {
  const match = useRouteMatch<{ pageId: string }>(
    "/settings/pages/edit/:pageId",
  );
  const pageId = Number(match?.params.pageId);

  const { page, isLoading: isPageLoading } = usePage(pageId);
  const {
    currentCanvasAppearance,
    currentCanvasTheme,
    updateCurrentCanvasThemeVariables,
    toggleCurrentCanvasTheme,
  } = useThemeFormHelpers();

  const { show: showPageThemeConfirmationModal } =
    usePageThemeConfirmationModal();
  const { show: showCustomThemeConfirmationModal } =
    useCustomThemeConfirmationModal();
  const { show: showSiteThemeConfirmationModal } =
    useSiteThemeConfirmationModal();
  const isCustomFontsEnabled = useIsCustomFontsEnabled();

  const formMethodsRef = useRef<UseFormReturn<FormData>>();
  const pageThemeVariables = page?.theme.css_variables;
  const currentCanvasThemeVariables = currentCanvasTheme.cssVariables;

  const getCurrentGoogleFonts = useCallback(() => {
    const formData = formMethodsRef.current?.getValues();
    return deriveGoogleFontsFromFormData(formData?.theme || {});
  }, [formMethodsRef]);

  const processVariableUpdate = useCallback(
    (fieldValue: string, variableName: string) => {
      const variableLookup = CSS_VARIABLE_LOOKUP[variableName];

      if (!variableLookup) return;

      const key = variableLookup.category;
      const cssVariable = variableLookup.variable;

      if (!key || !cssVariable) return;

      if (key === "appearance") {
        updateCurrentCanvasThemeVariables(
          currentCanvasAppearance,
          cssVariable,
          fieldValue,
        );
      } else {
        updateCurrentCanvasThemeVariables(key, cssVariable, fieldValue);
      }
    },
    [updateCurrentCanvasThemeVariables, currentCanvasAppearance],
  );

  const handleFormReady = useCallback((methods: UseFormReturn<FormData>) => {
    formMethodsRef.current = methods;
  }, []);

  const handleDiscard = useCallback(() => {
    if (isPageLoading || !page) return;

    const { id, theme_name, css_variables } = page.theme;

    toggleCurrentCanvasTheme({
      value: id,
      name: theme_name,
      cssVariables: css_variables,
    });

    formMethodsRef.current?.reset({
      theme: css_variables,
    });
  }, [isPageLoading, page, toggleCurrentCanvasTheme]);

  const handleThemeUpdate = useCallback(
    (scope: "page" | "site") => {
      if (!formMethodsRef.current) return;
      const isDirty = formMethodsRef.current.formState.isDirty;

      if (isDirty) {
        void showCustomThemeConfirmationModal({
          appearance: currentCanvasAppearance,
          cssVariables: currentCanvasThemeVariables,
          existingThemeId: currentCanvasTheme.isCustom
            ? currentCanvasTheme.value
            : undefined,
          name: currentCanvasTheme.name,
          reset: (values?: { theme: CssVariables }) => {
            if (values) {
              formMethodsRef.current?.reset(values);
            } else {
              formMethodsRef.current?.reset();
            }
          },
          scope,
          toggleCurrentCanvasTheme,
          googleFonts: isCustomFontsEnabled ? getCurrentGoogleFonts() : [],
        });
      } else if (scope === "page") {
        void showPageThemeConfirmationModal({
          currentCanvasTheme,
          appearance: currentCanvasAppearance,
          pageId,
        });
      } else {
        void showSiteThemeConfirmationModal({
          currentCanvasTheme,
          appearance: currentCanvasAppearance,
          cssVariables: currentCanvasThemeVariables,
        });
      }
    },
    [
      isCustomFontsEnabled,
      currentCanvasTheme,
      currentCanvasAppearance,
      currentCanvasThemeVariables,
      toggleCurrentCanvasTheme,
      pageId,
      showCustomThemeConfirmationModal,
      showPageThemeConfirmationModal,
      showSiteThemeConfirmationModal,
      getCurrentGoogleFonts,
    ],
  );

  const handlePageThemUpdate = useCallback(
    () => handleThemeUpdate("page"),
    [handleThemeUpdate],
  );

  const handleSiteThemeUpdate = useCallback(
    () => handleThemeUpdate("site"),
    [handleThemeUpdate],
  );

  const handleValueChange = useCallback(
    (methods: UseFormReturn<FormData>) => {
      if (!isEqual(pageThemeVariables, currentCanvasThemeVariables)) {
        methods.setValue("theme", currentCanvasThemeVariables, {
          shouldDirty: true,
        });
      }
    },
    [pageThemeVariables, currentCanvasThemeVariables],
  );

  const renderFooter = useCallback(
    () => (
      <FormWrapperFooter
        isPageLoading={isPageLoading}
        onDiscard={handleDiscard}
        onUpdatePageTheme={handlePageThemUpdate}
        onUpdateSiteTheme={handleSiteThemeUpdate}
      />
    ),
    [isPageLoading, handleDiscard, handlePageThemUpdate, handleSiteThemeUpdate],
  );

  return (
    <BaseThemeFormWrapper<FormData>
      defaultValues={{
        theme: pageThemeVariables || {},
      }}
      processVariableUpdate={processVariableUpdate}
      onFormReady={handleFormReady}
      onValueChange={handleValueChange}
      footerContent={renderFooter}
    >
      {children}
    </BaseThemeFormWrapper>
  );
};
